import { Box, Popover } from '@mui/material'
import ResponsiblePersonsBlock from 'components/cattles/trim/shared/filterContent/ResponsiblePersonsBlock'
import TrimContentBlock from 'components/cattles/trim/shared/filterContent/TrimContentBlock'
import { TreatmentStatus } from 'models'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'
import { defaultInitialState } from 'store/lamenessFilterSlice'
import { COLORS } from 'styles/colors'
import { ButtonUI } from 'ui/buttons/ButtonUI'
import CustomButton from 'ui/buttons/CustomButton'
import { TypographyHeader14 } from 'ui/typography/TypographyHeader14'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographyPrimary16Bold } from 'ui/typography/TypographyPrimary16Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { ReactComponent as CrossCloseLogo } from '../../../../../assets/cross_close_logo.svg'
import { ReactComponent as FilterIcon } from '../../../../../assets/icons/filter-icon.svg'
import CowAgeContentBlock from './CowAgeContentBlock'
import DiagnoseContentBlock from './DiagnoseContentBlock'
import ExaminersContentBlock from './ExaminersContentBlock'
import FatnessContentBlock from './FatnessContentBlock'
import GroupsContentBlock from './GroupsContentBlock'
import InspectionContentBlock from './InspectionContentBlock'
import LactationContentBlock from './LactationContentBlock'
import LamenessContentBlock from './LamenessContentBlock'
import TreatmentContentBlock from './TreatmentContentBlock'

const RANGE_SECTION_KEYS = [
	'lameness',
	'lactation',
	'cowAge',
	'treatmentStatus',
	'lastTrim',
	'fatness',
] as const

type RangeSectionKey = (typeof RANGE_SECTION_KEYS)[number]

const isRangeSectionKey = (key: string): key is RangeSectionKey =>
	(RANGE_SECTION_KEYS as readonly string[]).includes(key)

const isEmptyBound = (value: unknown) => value === '' || value == null

const isRangeEmpty = (range?: { startOf?: unknown; endOf?: unknown }) =>
	!range || (isEmptyBound(range.startOf) && isEmptyBound(range.endOf))

const hasAvailableBounds = (range?: { startOf?: unknown; endOf?: unknown }) =>
	Boolean(range && !isEmptyBound(range.startOf) && !isEmptyBound(range.endOf))

const isSameBound = (left: unknown, right: unknown) =>
	!isEmptyBound(left) && !isEmptyBound(right) && Number(left) === Number(right)

const isTreatmentDurationEnabled = (treatmentStatus?: {
	statuses?: string[]
}) =>
	Boolean(
		treatmentStatus?.statuses?.length &&
			treatmentStatus.statuses.every(
				status => status === TreatmentStatus.Treated,
			),
	)

const canSeedRange = (key: RangeSectionKey, filters) => {
	if (
		key === 'treatmentStatus' &&
		!isTreatmentDurationEnabled(filters?.treatmentStatus)
	) {
		return false
	}

	return isRangeEmpty(filters?.[key])
}

const seedRangeDefaults = (key: string, filters, available) => {
	if (!filters || !isRangeSectionKey(key) || !canSeedRange(key, filters)) {
		return filters
	}

	const availableRange = available?.[key]
	if (!hasAvailableBounds(availableRange)) return filters

	return {
		...filters,
		[key]: {
			...filters[key],
			startOf: availableRange.startOf,
			endOf: availableRange.endOf,
		},
	}
}

const seedAllRangeDefaults = (filters, available) =>
	RANGE_SECTION_KEYS.reduce(
		(next, key) => seedRangeDefaults(key, next, available),
		filters,
	)

const stripUntouchedRanges = (
	filters,
	available,
	touched: Set<RangeSectionKey>,
) => {
	if (!filters) return filters

	return RANGE_SECTION_KEYS.reduce((next, key) => {
		if (touched.has(key)) return next

		const current = next[key]
		const availableRange = available?.[key]
		const isSeeded =
			current &&
			hasAvailableBounds(availableRange) &&
			isSameBound(current.startOf, availableRange.startOf) &&
			isSameBound(current.endOf, availableRange.endOf)

		if (!isSeeded) return next

		return {
			...next,
			[key]: {
				...current,
				startOf: defaultInitialState[key].startOf,
				endOf: defaultInitialState[key].endOf,
			},
		}
	}, filters)
}

const getTouchedRanges = (filters): Set<RangeSectionKey> =>
	new Set(
		RANGE_SECTION_KEYS.filter(key => filters && !isRangeEmpty(filters[key])),
	)

const sameFilters = (left, right) =>
	JSON.stringify(left ?? null) === JSON.stringify(right ?? null)

const FilterContent = props => {
	const {
		activeFilters,
		availableFilters,
		setActiveFilters,
		openFilter,
		setOpenFilter,
		returnInitialProps,
		titleForFilter,
		isEqualFilter,
	} = props

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const [selectedCase, setSelectedCase] = useState('lameness')
	const [cowsCount, setCowsCount] = useState<number>(0)
	const [pendingFilters, setPendingFilters] = useState(activeFilters)
	const [touchedRanges, setTouchedRanges] = useState<Set<RangeSectionKey>>(() =>
		getTouchedRanges(activeFilters),
	)

	const markTouched = (key: string) => {
		if (!isRangeSectionKey(key)) return
		setTouchedRanges(prev => {
			if (prev.has(key)) return prev
			const next = new Set(prev)
			next.add(key)
			return next
		})
	}

	const unmarkTouched = (key: string) => {
		if (!isRangeSectionKey(key)) return
		setTouchedRanges(prev => {
			if (!prev.has(key)) return prev
			const next = new Set(prev)
			next.delete(key)
			return next
		})
	}

	const effectiveFilters = useMemo(
		() => stripUntouchedRanges(pendingFilters, availableFilters, touchedRanges),
		[availableFilters, pendingFilters, touchedRanges],
	)

	// Sync pendingFilters from activeFilters when opening
	const handleContentClick = (event: React.MouseEvent<HTMLElement>) => {
		setPendingFilters(seedAllRangeDefaults(activeFilters, availableFilters))
		setTouchedRanges(getTouchedRanges(activeFilters))
		setAnchorEl(event.currentTarget)
		setOpenFilter(prev => !prev)
	}

	// Close without applying — activeFilters unchanged
	const handleClose = useCallback(() => {
		setOpenFilter(false)
	}, [setOpenFilter])

	// Apply pending to active and close
	const handleApply = useCallback(() => {
		if (sameFilters(effectiveFilters, activeFilters)) return
		setActiveFilters(effectiveFilters)
		setOpenFilter(false)
	}, [activeFilters, effectiveFilters, setActiveFilters, setOpenFilter])

	// Reset all pending filters
	const handleResetAll = useCallback(() => {
		setTouchedRanges(new Set())
		setPendingFilters(
			seedAllRangeDefaults(defaultInitialState, availableFilters),
		)
	}, [availableFilters])

	// Reset specific category in pending filters
	const handleResetCategory = useCallback(
		(key: string) => {
			unmarkTouched(key)
			setPendingFilters(prev =>
				seedRangeDefaults(
					key,
					{
						...prev,
						[key]: defaultInitialState[key],
					},
					availableFilters,
				),
			)
		},
		[availableFilters],
	)

	// Local toggle for pending filters
	const pendingToggleFilter = useCallback(
		(property, key, replaceProp?, keyForReplace?) => {
			setPendingFilters(prev => {
				const isKeyPresent = prev[property].statuses.includes(key)
				const updatedStatuses = isKeyPresent
					? prev[property].statuses.filter(status => status !== key)
					: [...prev[property].statuses, key]

				const updatedReplaceStatuses =
					replaceProp && keyForReplace
						? keyForReplace.every(k => prev[replaceProp].statuses.includes(k))
							? prev[replaceProp].statuses.filter(
									status => !keyForReplace.includes(status),
								)
							: [...prev[replaceProp].statuses, ...keyForReplace]
						: []

				const nextFilters = replaceProp
					? {
							...prev,
							[replaceProp]: {
								...prev[replaceProp],
								statuses: updatedReplaceStatuses,
							},
							[property]: { ...prev[property], statuses: updatedStatuses },
						}
					: {
							...prev,
							[property]: { ...prev[property], statuses: updatedStatuses },
						}

				return property === 'treatmentStatus'
					? seedRangeDefaults(property, nextFilters, availableFilters)
					: nextFilters
			})
		},
		[availableFilters],
	)

	// Local handleDayChange for pending filters
	const pendingHandleDayChange = useCallback((e, property, key) => {
		if (!e || !e.target) return
		markTouched(property)
		const normalized = String(e.target.value).replace(',', '.')
		let newValue = normalized.replace(/[^0-9.]/g, '')
		if (property === 'cowAge') {
			const [integerPart, ...decimalParts] = newValue.split('.')
			newValue =
				decimalParts.length > 0
					? `${integerPart}.${decimalParts.join('')}`
					: integerPart
		} else {
			newValue = newValue.replace(/\./g, '')
		}
		setPendingFilters(prev => ({
			...prev,
			[property]: {
				...prev[property],
				[key]: newValue,
			},
		}))
	}, [])

	const pendingFatnessChange = (key: 'startOf' | 'endOf', value: number) => {
		markTouched('fatness')
		setPendingFilters(prev => {
			const nextFatness = { ...prev.fatness, [key]: value }
			const hasBoth =
				!isEmptyBound(nextFatness.startOf) && !isEmptyBound(nextFatness.endOf)

			if (hasBoth && Number(nextFatness.startOf) > Number(nextFatness.endOf)) {
				return {
					...prev,
					fatness: { startOf: nextFatness.endOf, endOf: nextFatness.startOf },
				}
			}

			return { ...prev, fatness: nextFatness }
		})
	}

	// Local turnOnFilters for pending filters
	const pendingTurnOnFilters = useCallback(
		(property: string, keys: string[]) => {
			setPendingFilters(prev => ({
				...prev,
				[property]: {
					...prev[property],
					statuses: [...prev[property].statuses, ...keys],
				},
			}))
		},
		[],
	)

	useEffect(() => {
		if (!openFilter) return
		setPendingFilters(prev => seedAllRangeDefaults(prev, availableFilters))
	}, [availableFilters, openFilter])

	useEffect(() => {
		const treatment = pendingFilters?.treatmentStatus
		if (
			!treatment ||
			isTreatmentDurationEnabled(treatment) ||
			!isRangeEmpty(treatment)
		) {
			return
		}

		unmarkTouched('treatmentStatus')
	}, [pendingFilters])

	useEffect(() => {
		if (openFilter && effectiveFilters) {
			CattlesApiService.getCowsCount(effectiveFilters).then(res => {
				setCowsCount(res.data)
			})
		}
	}, [effectiveFilters, openFilter])

	const isPendingEqualFilter = sameFilters(
		defaultInitialState,
		effectiveFilters,
	)
	const isPendingEqualLameness = sameFilters(
		defaultInitialState.lameness,
		effectiveFilters?.lameness,
	)
	const isPendingEqualTreatment = sameFilters(
		defaultInitialState.treatmentStatus,
		effectiveFilters?.treatmentStatus,
	)
	const isPendingEqualInspection = sameFilters(
		defaultInitialState.inspectionStatus,
		effectiveFilters?.inspectionStatus,
	)
	const isPendingEqualLactation = sameFilters(
		defaultInitialState.lactation,
		effectiveFilters?.lactation,
	)
	const isPendingEqualCowAge = sameFilters(
		defaultInitialState.cowAge,
		effectiveFilters?.cowAge,
	)
	const isPendingEqualGroups = sameFilters(
		defaultInitialState.groups,
		effectiveFilters?.groups,
	)
	const isPendingEqualFatness = sameFilters(
		defaultInitialState.fatness,
		effectiveFilters?.fatness,
	)
	const isPendingEqualExaminers = sameFilters(
		defaultInitialState.examiners,
		effectiveFilters?.examiners,
	)
	const isPendingEqualDiagnosis = sameFilters(
		defaultInitialState.diagnoses,
		effectiveFilters?.diagnoses,
	)
	const isPendingEqualTrim = sameFilters(
		defaultInitialState.lastTrim,
		effectiveFilters?.lastTrim,
	)
	const isPendingEqualRespPersons = sameFilters(
		defaultInitialState.responsiblePersons,
		effectiveFilters?.responsiblePersons,
	)
	const pendingFiltersChanged = !sameFilters(activeFilters, effectiveFilters)

	const renderContent = () => {
		switch (selectedCase) {
			case 'lameness':
				return (
					<LamenessContentBlock
						toggleFilter={pendingToggleFilter}
						availableLamenessValues={availableFilters?.lameness}
						lameness={pendingFilters?.lameness}
						handleDayChange={pendingHandleDayChange}
					/>
				)
			case 'treatmentStatus':
				return (
					<TreatmentContentBlock
						toggleFilter={pendingToggleFilter}
						availableTreatmentValues={availableFilters?.treatmentStatus}
						treatmentStatus={pendingFilters?.treatmentStatus}
						handleDayChange={pendingHandleDayChange}
						setActiveFilters={setPendingFilters}
					/>
				)
			case 'inspectionStatus':
				return (
					<InspectionContentBlock
						toggleFilter={pendingToggleFilter}
						inspectionStatus={pendingFilters?.inspectionStatus}
						availableInspectionDates={availableFilters?.inspectionStatus}
						setActiveFilters={setPendingFilters}
					/>
				)
			case 'diagnoses':
				return (
					<DiagnoseContentBlock
						setActiveFilters={setPendingFilters}
						diagnoses={pendingFilters?.diagnoses}
					/>
				)
			case 'lactation':
				return (
					<LactationContentBlock
						toggleFilter={pendingToggleFilter}
						lactation={pendingFilters?.lactation}
						availableLactationValues={availableFilters?.lactation}
						setActiveFilters={setPendingFilters}
						handleDayChange={pendingHandleDayChange}
						turnOnFilters={pendingTurnOnFilters}
						selectedCase={selectedCase}
					/>
				)
			case 'groups':
				return (
					<GroupsContentBlock
						setActiveFilters={setPendingFilters}
						groups={pendingFilters?.groups}
					/>
				)
			case 'cowAge':
				return (
					<CowAgeContentBlock
						availableAgeValues={availableFilters?.cowAge}
						handleDayChange={pendingHandleDayChange}
						cowAge={pendingFilters?.cowAge}
					/>
				)
			case 'fatness':
				return (
					<FatnessContentBlock
						fatness={pendingFilters?.fatness}
						availableFatnessValues={availableFilters?.fatness}
						onChange={pendingFatnessChange}
					/>
				)
			case 'examiners':
				return (
					<ExaminersContentBlock
						setActiveFilters={setPendingFilters}
						examiners={pendingFilters?.examiners}
					/>
				)
			case 'lastTrim':
				return (
					<TrimContentBlock
						availableLastTrimValues={availableFilters?.lastTrim}
						lastTrim={pendingFilters?.lastTrim}
						handleDayChange={pendingHandleDayChange}
						setActiveFilters={setPendingFilters}
					/>
				)
			case 'responsiblePersons':
				return (
					<ResponsiblePersonsBlock
						setActiveFilters={setPendingFilters}
						responsiblePersons={pendingFilters?.responsiblePersons}
					/>
				)
		}
	}

	return (
		<>
			<Box display='flex' alignItems='center' gap='8px'>
				<CustomButton
					title={'Фильтр'}
					onClick={handleContentClick}
					iconElement={
						<Box sx={{ position: 'relative' }}>
							<FilterIcon />
							{!isEqualFilter && (
								<Box
									component={'span'}
									sx={{
										position: 'absolute',
										width: '10px',
										height: '10px',
										borderRadius: '50%',
										backgroundColor: COLORS.health,
										bottom: '7px',
										right: '-4px',
									}}
								/>
							)}
						</Box>
					}
					colors={[COLORS.main, '#fff']}
				/>
				{!isEqualFilter && (
					<CrossCloseLogo
						onClick={() => returnInitialProps('all')}
						style={{
							width: '16px',
							height: '16px',
							cursor: 'pointer',
							stroke: COLORS.secondary,
						}}
					/>
				)}
			</Box>
			<Popover
				open={openFilter}
				onClose={handleClose}
				anchorEl={anchorEl}
				transitionDuration={200}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				transformOrigin={{ horizontal: 'right', vertical: -8 }}
				sx={{
					'.MuiPopover-paper': {
						width: '47vw',
						boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.15)',
						padding: '16px 24px',
						borderRadius: '8px',
					},
				}}
			>
				<Box
					display={'flex'}
					flexDirection={'row'}
					justifyContent={'space-between'}
					alignItems={'center'}
				>
					<TypographyPrimary16Bold>Фильтр</TypographyPrimary16Bold>
					<CrossCloseLogo
						style={{ cursor: 'pointer', stroke: COLORS.icon }}
						onClick={handleClose}
					/>
				</Box>
				<Box
					display={'flex'}
					flexDirection={'row'}
					height={'387px'}
					width={'100%'}
					marginTop={'24px'}
				>
					<ul
						style={{
							listStyleType: 'none',
							minWidth: '180px',
							borderRight: `1px solid ${COLORS.inputBorder}`,
						}}
					>
						{titleForFilter?.map(el => (
							<li
								key={el.id}
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									justifyContent: 'space-between',
									padding: '7px 0px',
									flexWrap: 'wrap',
								}}
							>
								<TypographyPrimary
									onClick={() => setSelectedCase(el.key)}
									sx={{
										color: selectedCase === el.key ? COLORS.main : 'none',
										position: 'relative',
										cursor: 'pointer',
									}}
								>
									{el.title}
								</TypographyPrimary>

								{((el.key === 'lameness' && !isPendingEqualLameness) ||
									(el.key === 'treatmentStatus' && !isPendingEqualTreatment) ||
									(el.key === 'inspectionStatus' &&
										!isPendingEqualInspection) ||
									(el.key === 'cowAge' && !isPendingEqualCowAge) ||
									(el.key === 'groups' && !isPendingEqualGroups) ||
									(el.key === 'lactation' && !isPendingEqualLactation) ||
									(el.key === 'fatness' && !isPendingEqualFatness) ||
									(el.key === 'examiners' && !isPendingEqualExaminers) ||
									(el.key === 'lastTrim' && !isPendingEqualTrim) ||
									(el.key === 'responsiblePersons' &&
										!isPendingEqualRespPersons) ||
									(el.key === 'diagnoses' && !isPendingEqualDiagnosis)) && (
									<CrossCloseLogo
										style={{
											cursor: 'pointer',
											width: '16px',
											height: '12px',
											marginRight: '8px',
										}}
										stroke={COLORS.secondary}
										onClick={() => handleResetCategory(el.key)}
									/>
								)}
							</li>
						))}
					</ul>
					<Box sx={{ marginLeft: '24px', width: '75%' }}>
						<TypographyHeader14 sx={{ marginBottom: '16px' }}>
							{titleForFilter?.find(el => el.key === selectedCase).title}
						</TypographyHeader14>
						{renderContent()}
					</Box>
				</Box>
				<Box
					display={'flex'}
					flexDirection={'column'}
					alignItems={'flex-end'}
					justifyContent={'flex-end'}
				>
					<TypographySecondary>Результат: {cowsCount}</TypographySecondary>
					<Box
						display={'flex'}
						flexDirection={'row'}
						alignItems={'center'}
						justifyContent={'flex-end'}
						width={'100%'}
						mt={'16px'}
						gap={'16px'}
					>
						{!isPendingEqualFilter && (
							<TypographySecondary
								onClick={handleResetAll}
								sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
							>
								Сбросить всё
							</TypographySecondary>
						)}
						<ButtonUI
							disabled={!pendingFiltersChanged}
							sx={{
								marginTop: '0px',
								width: '400px',
								height: '56px',
								fontWeight: 600,
								fontSize: '14px',
								lineHeight: '17px',
							}}
							onClick={handleApply}
						>
							Применить
						</ButtonUI>
					</Box>
				</Box>
			</Popover>
		</>
	)
}

export default FilterContent
